import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Transaction, TransactionType, Account, Category, BulkUpdateTransactionsPatch, TransactionSortField, SortOrder } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { plusSign } from '../lib/amount-format'
import { transactionsApi, accountsApi, categoriesApi, csvApi } from '../lib/api'
import { TransferFields, type TransferSource } from '../components/TransferFields'
import { TransferCounterpartNotice } from '../components/TransferCounterpartNotice'
import { DateRangePicker } from '../components/DateRangePicker'
import { AmountRangePicker } from '../components/AmountRangePicker'
import { CategoryCombobox } from '../components/CategoryCombobox'
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect'
import { ACCOUNT_ROLE_KEYS, ACCOUNT_TYPE_KEYS, accountRole } from '../lib/transfer'
import { useModalReset } from '../lib/use-modal-reset'
import { btnDanger, btnPrimary, btnSecondary, modalTitle, pageSubtitle, pageTitle, typeToggleGroup, typeToggleItem } from '../lib/ui'

/** 口座・種別・カテゴリの複数指定はカンマ区切りの URL クエリで受け渡す */
const splitCsvParam = (value: string | null) =>
  (value ?? '').split(',').map((v) => v.trim()).filter((v) => v.length > 0)

/** URL クエリで受け取れる絞り込み条件のキー（年間収支表などからの遷移で使う） */
const FILTER_PARAM_KEYS = ['accountId', 'toAccountId', 'type', 'category', 'dateFrom', 'dateTo', 'amountMin', 'amountMax', 'q', 'excluded'] as const

/** 集計対象外の取引を一覧に出すかどうか */
type ExcludedFilter = 'include' | 'exclude' | 'only'

interface UrlFilters {
  /** 空配列は全口座 */
  accounts: string[]
  /** 振替先口座。空配列は全振替先 */
  toAccounts: string[]
  types: TransactionType[]
  /** 空配列は全カテゴリ */
  categories: string[]
  dateFrom: string
  dateTo: string
  amountMin: string
  amountMax: string
  query: string
  excluded: ExcludedFilter
}

/**
 * URL クエリを絞り込み条件に変換する。条件のキーが一つも無ければ null を返し、
 * 画面側の絞り込みには触れない（通常の「取引」タブからの遷移を壊さないため）。
 */
function readUrlFilters(params: URLSearchParams): UrlFilters | null {
  if (!FILTER_PARAM_KEYS.some((key) => params.has(key))) return null
  const types = splitCsvParam(params.get('type'))
    .filter((v): v is TransactionType => v === 'income' || v === 'expense' || v === 'transfer')
  return {
    accounts: splitCsvParam(params.get('accountId')),
    toAccounts: splitCsvParam(params.get('toAccountId')),
    types,
    categories: splitCsvParam(params.get('category')),
    dateFrom: params.get('dateFrom') ?? '',
    dateTo: params.get('dateTo') ?? '',
    amountMin: params.get('amountMin') ?? '',
    amountMax: params.get('amountMax') ?? '',
    query: params.get('q') ?? '',
    excluded: params.get('excluded') === 'exclude' || params.get('excluded') === 'only'
      ? params.get('excluded') as ExcludedFilter
      : 'include',
  }
}

/**
 * 絞り込み条件を URL クエリに戻す。空の条件は落とし、キーの順番は
 * 年間収支表が組み立てる URL と揃えてあるので、遷移直後に書き換えが起きない。
 */
function buildFilterSearch(filters: UrlFilters): string {
  const params = new URLSearchParams()
  if (filters.accounts.length) params.set('accountId', filters.accounts.join(','))
  if (filters.toAccounts.length) params.set('toAccountId', filters.toAccounts.join(','))
  if (filters.types.length) params.set('type', filters.types.join(','))
  if (filters.categories.length) params.set('category', filters.categories.join(','))
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.amountMin) params.set('amountMin', filters.amountMin)
  if (filters.amountMax) params.set('amountMax', filters.amountMax)
  if (filters.query) params.set('q', filters.query)
  if (filters.excluded !== 'include') params.set('excluded', filters.excluded)
  return params.toString()
}

interface TransactionFormData {
  date: string
  description: string
  type: TransactionType
  category: string
  amount: string
  accountId: string
  toAccountId: string
  note: string
  excluded: boolean
  /** 着金側として紐付ける入金取引のID。選んでいなければ空文字 */
  counterpartIncomeId: string
}

const defaultFormData = (): TransactionFormData => ({
  date: new Date().toISOString().split('T')[0],
  description: '',
  type: 'expense',
  category: '',
  amount: '',
  accountId: '',
  toAccountId: '',
  note: '',
  excluded: false,
  counterpartIncomeId: '',
})

interface TransactionModalProps {
  open: boolean
  onClose: () => void
  onSaved: () => void
  token: string
  accounts: Account[]
  categories: Category[]
  transaction?: Transaction | null
}

function TransactionModal({ open, onClose, onSaved, token, accounts, categories, transaction }: TransactionModalProps) {
  const { t } = useLanguage()
  const [form, setForm] = useState<TransactionFormData>(defaultFormData())
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [unlinking, setUnlinking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useModalReset(open, transaction?.id ?? null, () => {
    if (transaction) {
      setForm({
        date: transaction.date,
        description: transaction.description,
        type: transaction.type,
        category: transaction.category,
        amount: String(transaction.amount),
        accountId: transaction.accountId,
        toAccountId: transaction.toAccountId ?? '',
        note: transaction.note ?? '',
        excluded: transaction.excluded ?? false,
        counterpartIncomeId: '',
      })
    } else {
      const fresh = defaultFormData()
      const firstCat = categories.find((c) => c.type === fresh.type)
      setForm({ ...fresh, category: firstCat?.id ?? '' })
    }
    setError(null)
  })

  const isEdit = !!transaction
  const filteredCategories = categories.filter((c) => c.type === form.type)
  // 振替のカテゴリは 1 つに決まるので選ばせない。保存時にこの ID を使う
  const transferCategoryId = categories.find((c) => c.type === 'transfer')?.id
  const isTransfer = form.type === 'transfer'
  // Wise / bitFlyer 連携が作る振替は振替先を持たない。そのまま編集できるよう必須にしない
  const destinationOptional = transaction?.type === 'transfer' && !transaction.toAccountId
  // 振替の着金側として紐付いているなら、その出金元の口座名。紐付いていなければ null
  const linkedFromAccount = transaction?.counterpartAccountId
    ? (accounts.find((a) => a.id === transaction.counterpartAccountId)?.name ?? null)
    : null

  const fromAccount = accounts.find((a) => a.id === form.accountId) ?? null
  const transferSource: TransferSource = {
    account: fromAccount,
    role: fromAccount ? accountRole(fromAccount.type) : null,
    name: fromAccount ? fromAccount.name : t('transactions_transfer_unselected'),
    caption: fromAccount ? t(ACCOUNT_TYPE_KEYS[fromAccount.type]) : null,
  }
  // 方向図に増減を出すための金額。未入力・不正な入力のときは出さない
  const parsedAmount = Number.parseFloat(form.amount)
  const transferAmount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : undefined

  // 口座は数が多いので種別名でも絞り込めるようにする。
  // 振替では種別で向きが決まるため、口座名だけでなく種別もラベルに出す
  const accountOptions = useMemo<SelectOption[]>(
    () => accounts.filter((a) => !a.isArchived).map((a) => {
      const typeLabel = t(ACCOUNT_TYPE_KEYS[a.type])
      return {
        value: a.id,
        label: isTransfer ? `${a.name}（${typeLabel}）` : a.name,
        keywords: typeLabel,
      }
    }),
    [accounts, isTransfer, t],
  )

  const handleCounterpartSelect = useCallback((id: string | null) => {
    setForm((f) => (f.counterpartIncomeId === (id ?? '') ? f : { ...f, counterpartIncomeId: id ?? '' }))
  }, [])

  const handleTypeChange = (type: TransactionType) => {
    const firstCat = categories.find((c) => c.type === type)
    setForm((f) => ({
      ...f,
      type,
      category: firstCat?.id ?? '',
      // 振替以外に戻したら振替先の紐付けは外す
      toAccountId: type === 'transfer' ? f.toAccountId : '',
      counterpartIncomeId: type === 'transfer' ? f.counterpartIncomeId : '',
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.accountId) {
      setError(t('transactions_form_account_required'))
      return
    }
    if (form.type === 'transfer' && !form.toAccountId && !destinationOptional) {
      setError(t('transactions_form_to_account_required'))
      return
    }
    setSubmitting(true)
    setError(null)
    const toAccountId = form.type === 'transfer' ? (form.toAccountId || null) : null
    // 振替は種別からカテゴリが決まる。連携由来などで別カテゴリが付いていても振替に寄せる
    const categoryToSave = isTransfer ? (transferCategoryId ?? form.category) : form.category
    try {
      if (transaction) {
        await transactionsApi.update(token, transaction.id, {
          date: form.date,
          description: form.description,
          type: form.type,
          category: categoryToSave,
          amount: parseFloat(form.amount),
          // 振替先を持たない連携由来の振替は、空のままなら現状を維持する（送らない）
          toAccountId: destinationOptional && toAccountId === null ? undefined : toAccountId,
          note: form.note || undefined,
          excluded: form.excluded,
          counterpartIncomeId: form.counterpartIncomeId || undefined,
        })
      } else {
        await transactionsApi.create(token, {
          date: form.date,
          description: form.description,
          type: form.type,
          category: categoryToSave,
          amount: parseFloat(form.amount),
          accountId: form.accountId,
          toAccountId,
          note: form.note || undefined,
          excluded: form.excluded,
          counterpartIncomeId: form.counterpartIncomeId || null,
        })
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * 振替の着金側としての紐付けを外す。取り違えて紐付いたときの逃げ道で、
   * 外すと収入に戻り、口座残高にも再び効くようになる。
   */
  const handleUnlink = async () => {
    if (!transaction) return
    setUnlinking(true)
    setError(null)
    try {
      await transactionsApi.update(token, transaction.id, { counterpartTransactionId: null })
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setUnlinking(false)
    }
  }

  const handleDelete = async () => {
    if (!transaction) return
    if (!confirm(t('transactions_delete_confirm'))) return
    setDeleting(true)
    setError(null)
    try {
      await transactionsApi.delete(token, transaction.id)
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_delete_error'))
    } finally {
      setDeleting(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 max-h-[90vh] overflow-y-auto dark:bg-gray-900">
        <h2 className={`${modalTitle} mb-4`}>
          {isEdit ? t('transactions_modal_edit_title') : t('transactions_modal_title')}
        </h2>
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          {linkedFromAccount && (
            <div className="rounded-lg border border-indigo-300 bg-indigo-50 p-3 text-xs dark:border-indigo-800 dark:bg-indigo-950/40">
              <p className="font-medium text-indigo-800 dark:text-indigo-300">
                {t('transactions_counterpart_linked_title').replace('{account}', linkedFromAccount)}
              </p>
              <p className="mt-0.5 text-indigo-800 dark:text-indigo-300">
                {t('transactions_counterpart_linked_hint')}
              </p>
              <button
                type="button"
                onClick={() => { void handleUnlink() }}
                disabled={unlinking}
                className="mt-2 text-indigo-700 underline underline-offset-2 disabled:opacity-50 dark:text-indigo-300"
              >
                {unlinking ? t('transactions_counterpart_unlinking') : t('transactions_counterpart_unlink')}
              </button>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('transactions_form_type')}</label>
            <div className={typeToggleGroup}>
              {(['expense', 'income', 'transfer'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeChange(type)}
                  className={typeToggleItem(type, form.type === type)}
                >
                  {type === 'expense' ? t('type_expense') : type === 'income' ? t('type_income') : t('type_transfer')}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('transactions_form_date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('transactions_form_description')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              placeholder={t('transactions_form_description_placeholder')}
            />
          </div>
          {!isTransfer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('transactions_form_category')}</label>
              <CategoryCombobox
                categories={filteredCategories}
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('transactions_form_amount')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              required
              min="0"
              step="any"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {form.type === 'transfer' ? t('transactions_form_from_account') : t('transactions_form_account')} <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={accountOptions}
              value={form.accountId}
              onChange={(v) => setForm((f) => ({ ...f, accountId: v }))}
              placeholder={t('transactions_form_account_placeholder')}
              searchPlaceholder={t('transactions_filter_search_accounts')}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
            />
          </div>
          {form.type === 'transfer' && (
            <div className="space-y-2">
              <TransferFields
                accounts={accounts}
                source={transferSource}
                value={form.toAccountId}
                onChange={(toAccountId) => setForm((f) => ({ ...f, toAccountId }))}
                required={!destinationOptional}
                placeholder={t('transactions_form_to_account_placeholder')}
                excludeIds={form.accountId ? [form.accountId] : []}
                amount={transferAmount}
              />
              {/* 相手方（振替先に同額が入っている取引）の裏取りと、着金側の選択。
                  選ばなければ紐付かないので、無くても保存はできる */}
              {!linkedFromAccount && (
                <TransferCounterpartNotice
                  token={token}
                  toAccountId={form.toAccountId}
                  amount={transferAmount}
                  date={form.date}
                  excludeId={transaction?.id}
                  selectedId={form.counterpartIncomeId || null}
                  onSelect={handleCounterpartSelect}
                />
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('transactions_form_note')}</label>
            <textarea
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              placeholder={t('transactions_form_note_placeholder')}
            />
          </div>
          {/* 集計対象外。残高には効かせたまま収支の集計からだけ外す */}
          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.excluded}
                onChange={(e) => setForm((f) => ({ ...f, excluded: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('transactions_form_excluded')}</span>
                <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{t('transactions_form_excluded_hint')}</span>
              </span>
            </label>
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex justify-between gap-2 pt-2">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={() => { void handleDelete() }}
                  disabled={deleting || submitting}
                  className={btnDanger}
                >
                  {deleting ? t('common_saving') : t('transactions_delete')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className={btnSecondary}
              >
                {t('common_cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting || deleting}
                className={btnPrimary}
              >
                {submitting ? t('common_saving') : t('common_save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

interface BulkEditModalProps {
  open: boolean
  count: number
  onClose: () => void
  /** 成功時は解決、失敗時は reject させてモーダル内にエラーを出す */
  onSubmit: (patch: BulkUpdateTransactionsPatch) => Promise<void>
  accounts: Account[]
  categories: Category[]
  /** 選択した取引の出金元口座（重複なし）。振替先の候補を絞るのに使う */
  fromAccounts: Account[]
}

/** 選択した取引の種別・カテゴリ・振替先だけをまとめて上書きするモーダル */
function BulkEditModal({ open, count, onClose, onSubmit, accounts, categories, fromAccounts }: BulkEditModalProps) {
  const { t } = useLanguage()
  // 空文字は「変更しない」を意味する
  const [type, setType] = useState<TransactionType | ''>('')
  const [category, setCategory] = useState('')
  const [toAccountId, setToAccountId] = useState('')
  // '' = 変更しない / 'on' = 集計対象外にする / 'off' = 集計対象に戻す
  const [excluded, setExcluded] = useState<'' | 'on' | 'off'>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useModalReset(open, null, () => {
    setType('')
    setCategory('')
    setToAccountId('')
    setExcluded('')
    setError(null)
  })

  // 種別を選んだらそのカテゴリだけに絞る。種別を変えない場合は全カテゴリから選べる
  const selectableCategories = type ? categories.filter((c) => c.type === type) : categories
  // 振替に変えるときだけ振替先を選ばせる。支出・収入のまま振替先を指定できると
  // 「支出のまま振替設定ができる」ように見えて紛らわしいため
  const showDestination = type === 'transfer'
  // 振替のカテゴリは 1 つに決まるので選ばせず、保存時に自動で当てる
  const transferCategoryId = categories.find((c) => c.type === 'transfer')?.id

  // 出金元は選択した取引側で決まっている。口座が1つに定まらなくても、
  // 種別（役割）が揃っていれば振替先の絞り込みには使える。
  const fromRoles = [...new Set(fromAccounts.map((a) => accountRole(a.type)))]
  const singleRole = fromRoles.length === 1 ? fromRoles[0] : null
  const transferSource: TransferSource =
    fromAccounts.length === 1
      ? {
          account: fromAccounts[0],
          role: accountRole(fromAccounts[0].type),
          name: fromAccounts[0].name,
          caption: t(ACCOUNT_TYPE_KEYS[fromAccounts[0].type]),
        }
      : {
          account: null,
          role: singleRole,
          mixed: fromAccounts.length > 1,
          name:
            fromAccounts.length > 0
              ? t('transactions_transfer_mixed_accounts').replace('{count}', String(fromAccounts.length))
              : t('transactions_transfer_unselected'),
          caption:
            fromAccounts.length === 0
              ? null
              : singleRole
              ? t(ACCOUNT_ROLE_KEYS[singleRole])
              : t('transactions_transfer_mixed_types'),
        }

  const handleTypeChange = (next: TransactionType | '') => {
    setType(next)
    setCategory('')
    if (next !== 'transfer') setToAccountId('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const patch: BulkUpdateTransactionsPatch = {}
    if (type !== '') patch.type = type
    if (showDestination) {
      // 振替へ変えるならカテゴリも振替に揃える
      if (transferCategoryId) patch.category = transferCategoryId
    } else if (category !== '') {
      patch.category = category
    }
    if (showDestination && toAccountId !== '') patch.toAccountId = toAccountId
    if (excluded !== '') patch.excluded = excluded === 'on'
    if (Object.keys(patch).length === 0) {
      setError(t('transactions_bulk_no_change'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(patch)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  const selectClass = 'border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 max-h-[90vh] overflow-y-auto dark:bg-gray-900">
        <h2 className={modalTitle}>
          {t('transactions_bulk_modal_title').replace('{count}', String(count))}
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('transactions_bulk_hint')}</p>
        <form onSubmit={(e) => { void handleSubmit(e) }} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('transactions_form_type')}</label>
            <SearchableSelect
              options={[
                { value: '', label: t('transactions_bulk_keep') },
                { value: 'income', label: t('type_income') },
                { value: 'expense', label: t('type_expense') },
                { value: 'transfer', label: t('type_transfer') },
              ]}
              value={type}
              onChange={(v) => handleTypeChange(v as TransactionType | '')}
              className={selectClass}
            />
          </div>
          {!showDestination && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('transactions_form_category')}</label>
              <CategoryCombobox
                categories={selectableCategories}
                value={category}
                onChange={setCategory}
                emptyLabel={t('transactions_bulk_keep')}
                className={selectClass}
              />
            </div>
          )}
          {showDestination && (
            <TransferFields
              accounts={accounts}
              source={transferSource}
              value={toAccountId}
              onChange={setToAccountId}
              required={false}
              placeholder={t('transactions_bulk_keep')}
              excludeIds={fromAccounts.map((a) => a.id)}
            />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('transactions_bulk_excluded_label')}</label>
            <SearchableSelect
              options={[
                { value: '', label: t('transactions_bulk_keep') },
                { value: 'on', label: t('transactions_bulk_excluded_on') },
                { value: 'off', label: t('transactions_bulk_excluded_off') },
              ]}
              value={excluded}
              onChange={(v) => setExcluded(v as '' | 'on' | 'off')}
              className={selectClass}
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={btnSecondary}
            >
              {t('common_cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={btnPrimary}
            >
              {submitting ? t('common_saving') : t('common_save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** カテゴリ名の左にアイコンを添えて表示する。未知の ID はそのまま出す */
function CategoryLabel({ categories, id }: { categories: Category[]; id: string }) {
  const category = categories.find((c) => c.id === id)
  if (!category) return <>{id}</>
  return (
    <span className="inline-flex items-center gap-1.5">
      {category.icon && <span aria-hidden>{category.icon}</span>}
      <span>{category.name}</span>
    </span>
  )
}

function accountName(accounts: Account[], id: string): string {
  return accounts.find((a) => a.id === id)?.name ?? id
}

interface TransactionsSummary {
  count: number
  income: number
  expense: number
  transferCount: number
  transferAmount: number
  /** 収入・支出の合計から外した集計対象外の件数 */
  excludedCount: number
  truncated: boolean
}

// 集計用フェッチの暴走を防ぐための上限（通常の個人利用では到達しない想定）
const SUMMARY_MAX_ITEMS = 5000
const SUMMARY_PAGE_SIZE = 200

// 集計中は値の位置に骨組みだけを表示し、枠自体は消さない
function SummaryStat({
  label,
  loading,
  className,
  children,
}: {
  label: string
  loading: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div className="text-right">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{label}</div>
      {loading ? (
        <div className="mt-2 mb-1 h-5 w-16 rounded bg-gray-200 animate-pulse ml-auto dark:bg-gray-700" />
      ) : (
        <div className={`mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100 ${className ?? ''}`}>{children}</div>
      )}
    </div>
  )
}

function SortableTh({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
  align = 'left',
  className = '',
}: {
  label: string
  field: TransactionSortField
  sortBy: TransactionSortField
  sortOrder: SortOrder
  onSort: (field: TransactionSortField) => void
  align?: 'left' | 'right'
  className?: string
}) {
  const active = sortBy === field
  return (
    <th
      onClick={() => onSort(field)}
      aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-3 py-1.5 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ${className}`}
    >
      {label}
      <span className={`ml-1 inline-block w-3 ${active ? '' : 'invisible'}`}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
    </th>
  )
}

export default function Transactions() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY, formatCurrency } = useAmountFormat()
  const [csvExporting, setCsvExporting] = useState(false)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(50)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [summary, setSummary] = useState<TransactionsSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)

  // 年間収支表などから ?category=...&dateFrom=... で飛んで来たときは、初回描画から反映する
  const [searchParams, setSearchParams] = useSearchParams()
  const initialFilters = useRef(readUrlFilters(searchParams)).current

  // 口座・種別・カテゴリはいずれも複数指定できる。空配列は「すべて」（絞り込まない）
  const [filterAccounts, setFilterAccounts] = useState<string[]>(initialFilters?.accounts ?? [])
  const [filterToAccounts, setFilterToAccounts] = useState<string[]>(initialFilters?.toAccounts ?? [])
  const [filterTypes, setFilterTypes] = useState<TransactionType[]>(initialFilters?.types ?? [])
  const [filterCategories, setFilterCategories] = useState<string[]>(initialFilters?.categories ?? [])
  const [filterDateFrom, setFilterDateFrom] = useState(initialFilters?.dateFrom ?? '')
  const [filterDateTo, setFilterDateTo] = useState(initialFilters?.dateTo ?? '')
  // 円単位の整数を表す文字列。未指定は空文字
  const [filterAmountMin, setFilterAmountMin] = useState(initialFilters?.amountMin ?? '')
  const [filterAmountMax, setFilterAmountMax] = useState(initialFilters?.amountMax ?? '')
  // 入力中の文字列と、実際に検索に使うキーワードを分けてデバウンスする
  const [searchInput, setSearchInput] = useState(initialFilters?.query ?? '')
  const [filterQuery, setFilterQuery] = useState(initialFilters?.query ?? '')
  const [filterExcluded, setFilterExcluded] = useState<ExcludedFilter>(initialFilters?.excluded ?? 'include')
  const [sortBy, setSortBy] = useState<TransactionSortField>('date')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // 同じ列を再クリックしたら昇順/降順を反転、別の列なら列を切り替えてページも先頭に戻す
  const handleSort = (field: TransactionSortField) => {
    setOffset(0)
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder(field === 'description' ? 'asc' : 'desc')
    }
  }

  // URL と画面の絞り込みを双方向に同期する。最後にやり取りしたクエリを覚えておき、
  // 自分が書いた変化に反応して書き戻す無限ループを防ぐ
  const urlSearch = searchParams.toString()
  const syncedSearch = useRef(urlSearch)

  // URL → 画面。すでにこの画面に居るまま別の条件で遷移して来たとき
  //（例: 年間収支表の別セル、ブラウザの戻る）に追従する
  useEffect(() => {
    if (urlSearch === syncedSearch.current) return
    syncedSearch.current = urlSearch
    const next = readUrlFilters(new URLSearchParams(urlSearch))
    if (!next) return
    setOffset(0)
    setFilterAccounts(next.accounts)
    setFilterToAccounts(next.toAccounts)
    setFilterTypes(next.types)
    setFilterCategories(next.categories)
    setFilterDateFrom(next.dateFrom)
    setFilterDateTo(next.dateTo)
    setFilterAmountMin(next.amountMin)
    setFilterAmountMax(next.amountMax)
    setSearchInput(next.query)
    setFilterQuery(next.query)
    setFilterExcluded(next.excluded)
  }, [urlSearch])

  // 画面 → URL。画面で絞り込みを変えたら URL にも出して、リロードや共有で同じ結果に戻れるようにする。
  // キーワードはデバウンス後の値を使うので、入力のたびに URL が書き換わることはない
  useEffect(() => {
    const next = buildFilterSearch({
      accounts: filterAccounts,
      toAccounts: filterToAccounts,
      types: filterTypes,
      categories: filterCategories,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      amountMin: filterAmountMin,
      amountMax: filterAmountMax,
      query: filterQuery,
      excluded: filterExcluded,
    })
    if (next === syncedSearch.current) return
    syncedSearch.current = next
    // 絞り込みの変更で履歴が積み上がらないよう、現在のエントリを置き換える
    setSearchParams(next, { replace: true })
  }, [
    filterAccounts,
    filterToAccounts,
    filterTypes,
    filterCategories,
    filterDateFrom,
    filterDateTo,
    filterAmountMin,
    filterAmountMax,
    filterQuery,
    filterExcluded,
    setSearchParams,
  ])

  useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(0)
      setFilterQuery(searchInput.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const fetchTransactions = useCallback(() => {
    if (!accessToken) return
    setLoading(true)
    transactionsApi.list(accessToken, {
      accountId: filterAccounts.length ? filterAccounts : undefined,
      toAccountId: filterToAccounts.length ? filterToAccounts : undefined,
      type: filterTypes.length ? filterTypes : undefined,
      category: filterCategories.length ? filterCategories : undefined,
      dateFrom: filterDateFrom || undefined,
      dateTo: filterDateTo || undefined,
      amountMin: filterAmountMin ? Number(filterAmountMin) : undefined,
      amountMax: filterAmountMax ? Number(filterAmountMax) : undefined,
      q: filterQuery || undefined,
      excluded: filterExcluded,
      sortBy,
      sortOrder,
      limit,
      offset,
    })
      .then((res) => {
        setTransactions(res.data)
        setTotal(res.meta.total)
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setLoading(false))
  }, [accessToken, filterAccounts, filterToAccounts, filterTypes, filterCategories, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, filterQuery, filterExcluded, sortBy, sortOrder, limit, offset, t])

  useEffect(() => {
    fetchTransactions()
  }, [fetchTransactions])

  // 絞り込み条件に一致する全件（表示中のページに限らない）を取得し、
  // 合計・平均などの集計はここで取得した金額をもとにフロントで計算する
  const summaryRequestIdRef = useRef(0)

  const fetchSummary = useCallback(async () => {
    if (!accessToken) return
    const requestId = ++summaryRequestIdRef.current
    const filters = {
      accountId: filterAccounts.length ? filterAccounts : undefined,
      toAccountId: filterToAccounts.length ? filterToAccounts : undefined,
      type: filterTypes.length ? filterTypes : undefined,
      category: filterCategories.length ? filterCategories : undefined,
      dateFrom: filterDateFrom || undefined,
      dateTo: filterDateTo || undefined,
      amountMin: filterAmountMin ? Number(filterAmountMin) : undefined,
      amountMax: filterAmountMax ? Number(filterAmountMax) : undefined,
      q: filterQuery || undefined,
      excluded: filterExcluded,
    }
    setSummaryLoading(true)
    try {
      let fetchOffset = 0
      let totalCount = 0
      let count = 0
      let income = 0
      let expense = 0
      let transferCount = 0
      let transferAmount = 0
      let excludedCount = 0
      do {
        const res = await transactionsApi.list(accessToken, {
          ...filters,
          limit: SUMMARY_PAGE_SIZE,
          offset: fetchOffset,
        })
        // フィルタ変更で新しいリクエストが発火済みなら、この古いリクエストの結果は破棄する
        if (requestId !== summaryRequestIdRef.current) return
        totalCount = res.meta.total
        for (const tx of res.data) {
          count += 1
          // 集計対象外は残高には効いているが収支ではないので、合計には足さない
          if (tx.excluded) excludedCount += 1
          else if (tx.type === 'income') income += tx.amount
          else if (tx.type === 'expense') expense += tx.amount
          else { transferCount += 1; transferAmount += tx.amount }
        }
        fetchOffset += SUMMARY_PAGE_SIZE
      } while (fetchOffset < totalCount && fetchOffset < SUMMARY_MAX_ITEMS)
      if (requestId !== summaryRequestIdRef.current) return
      setSummary({ count, income, expense, transferCount, transferAmount, excludedCount, truncated: count < totalCount })
    } catch {
      if (requestId !== summaryRequestIdRef.current) return
      setSummary(null)
    } finally {
      if (requestId === summaryRequestIdRef.current) setSummaryLoading(false)
    }
  }, [accessToken, filterAccounts, filterToAccounts, filterTypes, filterCategories, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, filterQuery, filterExcluded])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    if (!accessToken) return
    accountsApi.list(accessToken, { includeArchived: false })
      .then((res) => setAccounts(res.data))
      .catch(() => undefined)
    categoriesApi.list(accessToken)
      .then((res) => setCategories(res.data))
      .catch(() => undefined)
  }, [accessToken])

  // 一覧が入れ替わったら、画面に出ていない取引の選択は落とす
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev
      const visible = new Set(transactions.map((tx) => tx.id))
      const next = new Set([...prev].filter((id) => visible.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [transactions])

  useEffect(() => {
    if (!notice) return
    const timer = setTimeout(() => setNotice(null), 5000)
    return () => clearTimeout(timer)
  }, [notice])

  const applyFilter = (fn: () => void) => {
    setOffset(0)
    fn()
  }

  const allSelected = transactions.length > 0 && transactions.every((tx) => selectedIds.has(tx.id))
  const someSelected = selectedIds.size > 0 && !allSelected

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(transactions.map((tx) => tx.id)))
  }

  const handleBulkUpdate = async (patch: BulkUpdateTransactionsPatch) => {
    if (!accessToken) return
    const res = await transactionsApi.bulkUpdate(accessToken, { ids: [...selectedIds], patch })
    setSelectedIds(new Set())
    setError(null)
    setNotice(t('transactions_bulk_updated').replace('{count}', String(res.data.updated)))
    fetchTransactions()
    fetchSummary()
  }

  const handleBulkDelete = async () => {
    if (!accessToken || selectedIds.size === 0) return
    const ids = [...selectedIds]
    if (!confirm(t('transactions_bulk_delete_confirm').replace('{count}', String(ids.length)))) return
    setBulkDeleting(true)
    setError(null)
    try {
      const res = await transactionsApi.bulkDelete(accessToken, ids)
      setSelectedIds(new Set())
      setNotice(t('transactions_bulk_deleted').replace('{count}', String(res.data.deleted)))
      // 末尾ページを消し切ったらページを戻す（offset が変われば再取得は effect 側で走る）
      const nextOffset = offset > 0 && offset >= total - res.data.deleted ? Math.max(0, offset - limit) : offset
      if (nextOffset !== offset) setOffset(nextOffset)
      else fetchTransactions()
      fetchSummary()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_delete_error'))
    } finally {
      setBulkDeleting(false)
    }
  }

  // チェックボックスで取引を選んでいるときは、選択した取引だけを集計する。
  // 選択はページ内の取引に限られる（一覧が入れ替わると選択は落ちる）ため、
  // 絞り込み全件を取り直さずに手元の行から計算できる。
  const selectedSummary = useMemo<TransactionsSummary | null>(() => {
    if (selectedIds.size === 0) return null
    let count = 0
    let income = 0
    let expense = 0
    let transferCount = 0
    let transferAmount = 0
    let excludedCount = 0
    for (const tx of transactions) {
      if (!selectedIds.has(tx.id)) continue
      count += 1
      if (tx.excluded) excludedCount += 1
      else if (tx.type === 'income') income += tx.amount
      else if (tx.type === 'expense') expense += tx.amount
      else { transferCount += 1; transferAmount += tx.amount }
    }
    return { count, income, expense, transferCount, transferAmount, excludedCount, truncated: false }
  }, [transactions, selectedIds])

  // 選択中はその集計を、未選択なら絞り込み全件の集計を表示する
  const displaySummary = selectedSummary ?? summary
  const displaySummaryLoading = selectedSummary === null && summaryLoading

  // 一括編集の振替先を絞り込むため、選択中の取引の出金元口座を重複なしで集める
  const selectedFromAccounts = useMemo(() => {
    const ids = new Set(transactions.filter((tx) => selectedIds.has(tx.id)).map((tx) => tx.accountId))
    return accounts.filter((a) => ids.has(a.id))
  }, [transactions, selectedIds, accounts])

  // 口座は数が多いので、種別名でも絞り込めるようにしておく
  const accountFilterOptions = useMemo<SelectOption[]>(() => [
    { value: '', label: t('transactions_filter_all_accounts') },
    ...accounts.map((a) => ({ value: a.id, label: a.name, keywords: t(ACCOUNT_TYPE_KEYS[a.type]) })),
  ], [accounts, t])

  // 振替先を持つのは振替だけなので、この条件を指定すると実質的に振替へ絞り込まれる
  const toAccountFilterOptions = useMemo<SelectOption[]>(() => [
    { value: '', label: t('transactions_filter_all_to_accounts') },
    ...accounts.map((a) => ({ value: a.id, label: a.name, keywords: t(ACCOUNT_TYPE_KEYS[a.type]) })),
  ], [accounts, t])

  // 種別・カテゴリも複数選べるので、先頭の空の選択肢が「すべて」に戻すボタンになる
  const typeFilterOptions: SelectOption[] = [
    { value: '', label: t('transactions_filter_all_types') },
    { value: 'income', label: t('type_income') },
    { value: 'expense', label: t('type_expense') },
    { value: 'transfer', label: t('type_transfer') },
  ]

  const categoryFilterOptions = useMemo<SelectOption[]>(() => [
    { value: '', label: t('transactions_filter_all_categories') },
    ...categories.map((c) => ({ value: c.id, label: c.name, icon: c.icon })),
  ], [categories, t])

  const excludedFilterOptions: SelectOption[] = [
    { value: 'include', label: t('transactions_filter_excluded_all') },
    { value: 'exclude', label: t('transactions_filter_excluded_exclude') },
    { value: 'only', label: t('transactions_filter_excluded_only') },
  ]

  // 何か絞り込まれているときだけクリアボタンを出す
  const hasActiveFilter = filterAccounts.length > 0
    || filterToAccounts.length > 0
    || filterTypes.length > 0
    || filterCategories.length > 0
    || filterDateFrom !== ''
    || filterDateTo !== ''
    || filterAmountMin !== ''
    || filterAmountMax !== ''
    || searchInput !== ''
    || filterQuery !== ''
    || filterExcluded !== 'include'

  const clearFilters = () => applyFilter(() => {
    setFilterAccounts([])
    setFilterToAccounts([])
    setFilterTypes([])
    setFilterCategories([])
    setFilterDateFrom('')
    setFilterDateTo('')
    setFilterAmountMin('')
    setFilterAmountMax('')
    setSearchInput('')
    setFilterQuery('')
    setFilterExcluded('include')
  })

  const totalPages = Math.ceil(total / limit)
  const currentPage = Math.floor(offset / limit) + 1

  const paginationLabel = t('transactions_pagination')
    .replace('{total}', String(total))
    .replace('{page}', String(currentPage))
    .replace('{totalPages}', String(totalPages))

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={pageTitle}>{t('transactions_title')}</h1>
          <p className={pageSubtitle}>{t('transactions_subtitle')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => {
              if (!accessToken) return
              setCsvExporting(true)
              csvApi.exportTransactions(accessToken, {
                accountId: filterAccounts.length ? filterAccounts : undefined,
                toAccountId: filterToAccounts.length ? filterToAccounts : undefined,
                type: filterTypes.length ? filterTypes : undefined,
                category: filterCategories.length ? filterCategories : undefined,
                dateFrom: filterDateFrom || undefined,
                dateTo: filterDateTo || undefined,
                amountMin: filterAmountMin ? Number(filterAmountMin) : undefined,
                amountMax: filterAmountMax ? Number(filterAmountMax) : undefined,
                q: filterQuery || undefined,
                excluded: filterExcluded,
              }).finally(() => setCsvExporting(false))
            }}
            disabled={csvExporting}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {csvExporting ? t('transactions_csv_exporting') : t('transactions_csv_export')}
          </button>
          <button
            onClick={() => { setEditingTransaction(null); setModalOpen(true) }}
            className={btnPrimary}
          >
            {t('transactions_add')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex flex-wrap gap-3">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('transactions_filter_search')}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 flex-1 min-w-[200px] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <SearchableSelect
            multiple
            options={accountFilterOptions}
            value={filterAccounts}
            onChange={(v) => applyFilter(() => setFilterAccounts(v))}
            searchPlaceholder={t('transactions_filter_search_accounts')}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <SearchableSelect
            multiple
            options={toAccountFilterOptions}
            value={filterToAccounts}
            onChange={(v) => applyFilter(() => setFilterToAccounts(v))}
            searchPlaceholder={t('transactions_filter_search_to_accounts')}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <SearchableSelect
            multiple
            options={typeFilterOptions}
            value={filterTypes}
            onChange={(v) => applyFilter(() => setFilterTypes(v as TransactionType[]))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <SearchableSelect
            options={excludedFilterOptions}
            value={filterExcluded}
            onChange={(v) => applyFilter(() => setFilterExcluded(v as ExcludedFilter))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <SearchableSelect
            multiple
            options={categoryFilterOptions}
            value={filterCategories}
            onChange={(v) => applyFilter(() => setFilterCategories(v))}
            searchPlaceholder={t('category_search_placeholder')}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
          <DateRangePicker
            from={filterDateFrom}
            to={filterDateTo}
            onChange={(nextFrom, nextTo) => applyFilter(() => {
              setFilterDateFrom(nextFrom)
              setFilterDateTo(nextTo)
            })}
            className="min-w-[15rem]"
          />
          <AmountRangePicker
            min={filterAmountMin}
            max={filterAmountMax}
            types={filterTypes}
            onChange={(nextMin, nextMax, nextTypes) => applyFilter(() => {
              setFilterAmountMin(nextMin)
              setFilterAmountMax(nextMax)
              setFilterTypes(nextTypes)
            })}
            className="min-w-[11rem]"
          />
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className={btnSecondary}
            >
              {t('transactions_filter_clear')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {notice && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 dark:bg-green-950 dark:border-green-800 dark:text-green-400">
          {notice}
        </div>
      )}

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-900 dark:bg-indigo-950">
          <span className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
            {t('transactions_bulk_selected').replace('{count}', String(selectedIds.size))}
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className={btnSecondary}
            >
              {t('transactions_bulk_clear')}
            </button>
            <button
              onClick={() => setBulkModalOpen(true)}
              className={btnPrimary}
            >
              {t('transactions_bulk_edit')}
            </button>
            <button
              onClick={() => { void handleBulkDelete() }}
              disabled={bulkDeleting}
              className={btnDanger}
            >
              {bulkDeleting ? t('common_saving') : t('transactions_bulk_delete')}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex-1 min-h-0 bg-white rounded-xl shadow-sm border border-gray-100 overflow-auto dark:bg-gray-900 dark:border-gray-800">
        <table className="w-full min-w-[860px]">
          <thead className="bg-gray-50 sticky top-0 z-10 dark:bg-gray-800">
            <tr>
              <th className="w-10 px-3 py-1.5 text-left">
                <input
                  type="checkbox"
                  aria-label={t('transactions_bulk_select_all')}
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected }}
                  onChange={toggleSelectAll}
                  disabled={transactions.length === 0}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900"
                />
              </th>
              <SortableTh label={t('transactions_col_date')} field="date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableTh label={t('transactions_col_description')} field="description" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="w-full" />
              <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap dark:text-gray-400">{t('transactions_col_account')}</th>
              <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap dark:text-gray-400">{t('transactions_col_category')}</th>
              <SortableTh label={t('transactions_col_type')} field="type" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              <SortableTh label={t('transactions_col_amount')} field="amount" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} align="right" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                  {t('common_loading')}
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                  {t('transactions_no_result')}
                </td>
              </tr>
            ) : (
              transactions.map((tx) => {
                const formattedAmount = formatCurrency(tx.amount, tx.currency)
                const isExpense = tx.type === 'expense'
                const isTransfer = tx.type === 'transfer'
                const isExcluded = tx.excluded === true
                return (
                  <tr
                    key={tx.id}
                    onClick={() => { setEditingTransaction(tx); setModalOpen(true) }}
                    className={`border-t border-gray-100 cursor-pointer dark:border-gray-800 ${
                      selectedIds.has(tx.id)
                        ? 'bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-950'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {/* チェックボックスのクリックで編集モーダルが開かないように伝播を止める */}
                    <td className="w-10 px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(tx.id)}
                        onChange={() => toggleSelected(tx.id)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-sm text-gray-600 whitespace-nowrap dark:text-gray-300">{tx.date}</td>
                    <td className="px-3 py-1.5 text-sm text-gray-900 break-words dark:text-gray-100">
                      <div className="flex items-center gap-1.5">
                        {tx.isWise ? (
                          <span
                            title="Wise"
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-lime-200 text-lime-900 shrink-0"
                          >
                            Wise
                          </span>
                        ) : null}
                        {isExcluded && (
                          <span
                            title={t('transactions_excluded_badge_title')}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 shrink-0 dark:bg-amber-500/20 dark:text-amber-300"
                          >
                            {t('transactions_excluded_badge')}
                          </span>
                        )}
                        <span>{tx.description}</span>
                      </div>
                      {tx.note && (
                        <div className="mt-0.5 text-xs text-gray-500 whitespace-pre-line dark:text-gray-400">{tx.note}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-sm text-gray-500 whitespace-nowrap dark:text-gray-400">
                      {tx.counterpartAccountId ? (
                        // 振替の着金側。自分の口座は入金先なので、出金元を先に出す
                        <>
                          {accountName(accounts, tx.counterpartAccountId)}
                          <span className="whitespace-nowrap"> → {accountName(accounts, tx.accountId)}</span>
                        </>
                      ) : (
                        <>
                          {accountName(accounts, tx.accountId)}
                          {isTransfer && tx.toAccountId && (
                            <span className="whitespace-nowrap"> → {accountName(accounts, tx.toAccountId)}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-sm text-gray-500 whitespace-nowrap dark:text-gray-400">
                      <CategoryLabel categories={categories} id={tx.category} />
                    </td>
                    <td className="px-3 py-1.5 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          tx.type === 'income'
                            ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400'
                            : tx.type === 'expense'
                            ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {tx.type === 'income' ? t('type_income') : tx.type === 'expense' ? t('type_expense') : t('type_transfer')}
                      </span>
                    </td>
                    <td
                      className={`px-3 py-1.5 text-sm font-medium text-right whitespace-nowrap ${
                        isTransfer || isExcluded
                          ? 'text-gray-500 dark:text-gray-400'
                          : isExpense
                          ? 'text-red-600'
                          : 'text-green-600'
                      }`}
                    >
                      {isTransfer ? '' : isExpense ? '-' : '+'}{formattedAmount}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Summary。取引を選択中はその選択分、未選択なら絞り込み条件に一致する全件の合計・平均。
          計算中も枠は表示し続け、値だけをローディング表示に切り替える */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex flex-wrap justify-end gap-4">
          <SummaryStat label={t('transactions_summary_count')} loading={displaySummaryLoading}>
            {t('transactions_summary_unit').replace('{count}', String(displaySummary?.count ?? 0))}
          </SummaryStat>
          <SummaryStat label={t('transactions_summary_expense')} loading={displaySummaryLoading} className="text-red-600">
            -{formatJPY(displaySummary?.expense ?? 0)}
          </SummaryStat>
          <SummaryStat label={t('transactions_summary_income')} loading={displaySummaryLoading} className="text-green-600">
            +{formatJPY(displaySummary?.income ?? 0)}
          </SummaryStat>
          {!displaySummaryLoading && displaySummary && displaySummary.transferCount > 0 && (
            <SummaryStat label={t('transactions_summary_transfer')} loading={false} className="text-gray-500 dark:text-gray-400">
              {formatJPY(displaySummary.transferAmount)}
            </SummaryStat>
          )}
          <SummaryStat
            label={t('transactions_summary_net')}
            loading={displaySummaryLoading}
            className={displaySummary && displaySummary.income - displaySummary.expense >= 0 ? 'text-green-600' : 'text-red-600'}
          >
            {plusSign((displaySummary?.income ?? 0) - (displaySummary?.expense ?? 0))}{formatJPY((displaySummary?.income ?? 0) - (displaySummary?.expense ?? 0))}
          </SummaryStat>
          <SummaryStat label={t('transactions_summary_average')} loading={displaySummaryLoading}>
            {formatJPY(displaySummary && displaySummary.count > 0 ? (displaySummary.income - displaySummary.expense) / displaySummary.count : 0)}
          </SummaryStat>
        </div>
        {selectedSummary && (
          <p className="mt-3 text-right text-xs text-indigo-500 dark:text-indigo-400">
            {t('transactions_summary_selected_note').replace('{count}', String(selectedSummary.count))}
          </p>
        )}
        {!displaySummaryLoading && displaySummary && displaySummary.transferCount > 0 && (
          <p className="mt-3 text-right text-xs text-gray-400 dark:text-gray-500">
            {t('transactions_transfer_excluded_note')}
          </p>
        )}
        {!displaySummaryLoading && displaySummary && displaySummary.excludedCount > 0 && (
          <p className="mt-3 text-right text-xs text-gray-400 dark:text-gray-500">
            {t('transactions_excluded_note').replace('{count}', String(displaySummary.excludedCount))}
          </p>
        )}
        {displaySummary?.truncated && (
          <p className="mt-3 text-right text-xs text-gray-400 dark:text-gray-500">
            {t('transactions_summary_truncated').replace('{count}', String(displaySummary.count))}
          </p>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-gray-500 dark:text-gray-400">{paginationLabel}</p>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0) }}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {[50, 100, 200].map((n) => (
                <option key={n} value={n}>{n} {t('transactions_per_page')}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
              disabled={offset === 0}
              className={btnSecondary}
            >
              {t('transactions_prev')}
            </button>
            <button
              onClick={() => setOffset((o) => o + limit)}
              disabled={offset + limit >= total}
              className={btnSecondary}
            >
              {t('transactions_next')}
            </button>
          </div>
        </div>
      )}

      <TransactionModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTransaction(null) }}
        onSaved={() => { fetchTransactions(); fetchSummary() }}
        token={accessToken ?? ''}
        accounts={accounts}
        categories={categories}
        transaction={editingTransaction}
      />

      <BulkEditModal
        open={bulkModalOpen}
        count={selectedIds.size}
        onClose={() => setBulkModalOpen(false)}
        onSubmit={handleBulkUpdate}
        accounts={accounts}
        categories={categories}
        fromAccounts={selectedFromAccounts}
      />
    </div>
  )
}
